import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/services/api';
import { useToast } from '@/shared/toast';
import { DocumentCard } from './DocumentCard';
import {
  useDocumentActions,
  isInNativeWrapper,
  UPLOAD_ACCEPT,
  MAX_UPLOAD_MB,
  type DocumentItem,
  type DocumentSearchResult,
} from './useDocumentActions';

interface DocFolder {
  id: string;
  category: string;
  parentFolderId: string | null;
  name: string;
  hasContent: boolean;
}

interface PathSegment {
  id: string;
  name: string;
}

interface FolderModalState {
  mode: 'create' | 'rename';
  /** Presente solo in rinomina. */
  id?: string;
}

/**
 * Navigazione a cartelle per ODG/POS/Manuali (Rifornimenti resta piatto in
 * DocumentsPage). Livello corrente = currentFolderId (null = root). Mostra:
 * barra (ricerca + nuova cartella), breadcrumb navigabile, griglia cartelle
 * stile Windows (2 per fila) e, sotto, la lista file del livello (riusa
 * .doc-grid). La ricerca spazia su tutta la categoria e restituisce il percorso.
 */
export function FolderBrowser({ category }: { category: string }): React.JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [folderModal, setFolderModal] = useState<FolderModalState | null>(null);
  const [folderName, setFolderName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploading, uploadFiles, openDocument, deleteDocument, requestNativeUpload } =
    useDocumentActions({ category, folderId: currentFolderId });

  // Reset alla radice quando cambia categoria (tab): evita di restare "dentro"
  // una cartella di un'altra categoria.
  useEffect(() => {
    setCurrentFolderId(null);
    setSearchTerm('');
    setDebouncedSearch('');
  }, [category]);

  // Debounce ricerca ~300ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const searching = searchTerm.trim().length > 0;

  // Cartelle del livello corrente (parentFolderId assente = root).
  const { data: folders } = useQuery<DocFolder[]>({
    queryKey: ['doc-folders', category, currentFolderId],
    queryFn: async () => (await api.get('/documents/folders', {
      params: { category, parentFolderId: currentFolderId ?? undefined },
    })).data,
    enabled: !searching,
  });

  // File del livello corrente (folderId assente = root).
  const { data: documents } = useQuery<DocumentItem[]>({
    queryKey: ['documents', category, currentFolderId],
    queryFn: async () => (await api.get('/documents', {
      params: { category, folderId: currentFolderId ?? undefined },
    })).data,
    enabled: !searching,
  });

  // Breadcrumb (root -> cartella corrente): solo quando siamo dentro una cartella.
  const { data: pathSegments } = useQuery<PathSegment[]>({
    queryKey: ['doc-path', category, currentFolderId],
    queryFn: async () => (await api.get(`/documents/folders/${currentFolderId}/path`)).data,
    enabled: !!currentFolderId,
  });

  // Ricerca su tutta la categoria (debounced), con percorso testuale.
  const { data: searchResults, isFetching: searchFetching } = useQuery<DocumentSearchResult[]>({
    queryKey: ['doc-search', category, debouncedSearch],
    queryFn: async () => (await api.get('/documents', {
      params: { category, search: debouncedSearch },
    })).data,
    enabled: debouncedSearch.length > 0,
  });

  const segments = currentFolderId ? (pathSegments ?? []) : [];

  function goBack(): void {
    // Parent = penultimo segmento; se siamo in un livello top -> root.
    setCurrentFolderId(segments.length >= 2 ? segments[segments.length - 2].id : null);
  }

  function startUpload(): void {
    // App nativa: delega al wrapper (l'upload via WebView non funziona su Android),
    // inoltrando il folderId corrente. Browser: apre il normale <input type=file>.
    if (isInNativeWrapper()) {
      requestNativeUpload();
    } else {
      fileInputRef.current?.click();
    }
  }

  async function onInputChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    await uploadFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  }

  function openFolderModal(mode: 'create' | 'rename', folder?: DocFolder): void {
    setFolderModal({ mode, id: folder?.id });
    setFolderName(mode === 'rename' ? (folder?.name ?? '') : '');
  }

  async function submitFolder(): Promise<void> {
    if (!folderModal) return;
    const name = folderName.trim();
    if (!name) return;
    try {
      if (folderModal.mode === 'create') {
        await api.post('/documents/folders', { category, parentFolderId: currentFolderId, name });
      } else {
        await api.put(`/documents/folders/${folderModal.id}`, { name });
      }
      // Rinomina/creazione toccano la lista cartelle e i nomi nel breadcrumb.
      qc.invalidateQueries({ queryKey: ['doc-folders'] });
      qc.invalidateQueries({ queryKey: ['doc-path'] });
      setFolderModal(null);
      toast.show(folderModal.mode === 'create' ? 'Cartella creata' : 'Cartella rinominata');
    } catch (err) {
      // 400/409 dal server: messaggio esplicito (nome duplicato, ecc.).
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.show(msg ?? 'Errore salvataggio cartella', 'error');
    }
  }

  return (
    <>
      {/* Upload nel livello corrente — IN CIMA (layout richiesto da Bryan: prima il
          box di caricamento, poi sotto la ricerca + "crea cartella").
          Input file FUORI dalla .upload-area: il click() programmatico NON deve
          bubblare sull'onClick dell'area (bug noto, vedi DocumentsPage). */}
      <input ref={fileInputRef} type="file" onChange={onInputChange} disabled={uploading}
        accept={UPLOAD_ACCEPT} multiple style={{ display: 'none' }} />
      <div className={`upload-area${dragOver ? ' dragover' : ''}`}
        onClick={startUpload}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          void uploadFiles(Array.from(e.dataTransfer.files ?? []));
        }}>
        <div style={{ fontSize: 48, marginBottom: 15 }}>📤</div>
        <h3 style={{ marginBottom: 10 }}>Carica documento {category}</h3>
        {uploading ? (
          <p style={{ color: '#666', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span className="upload-spinner" />
            Caricamento in corso...
          </p>
        ) : (
          <p style={{ color: '#666' }}>
            Clicca o trascina un file (PDF, Word, Excel, TXT, CSV, Immagini — max {MAX_UPLOAD_MB} MB)
          </p>
        )}
      </div>

      {/* Barra: ricerca + nuova cartella (sotto il box upload). */}
      <div className="toolbar" style={{ marginTop: 16, gap: 10, flexWrap: 'wrap' }}>
        <input
          type="search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder={`🔍 Cerca in ${category}…`}
          style={{ flex: '1 1 220px', minWidth: 180, padding: '9px 12px',
                   border: '1px solid #ced4da', borderRadius: 8, boxSizing: 'border-box' }}
        />
        <button className="btn-success" onClick={() => openFolderModal('create')}>
          + Nuova cartella
        </button>
      </div>

      {searching ? (
        /* ---------- Vista RICERCA: lista piatta con percorso ---------- */
        <div className="doc-grid">
          {(searchResults ?? []).map(d => (
            <DocumentCard
              key={d.id}
              doc={d}
              meta={<>📁 {d.path || '(root)'}</>}
              onOpen={openDocument}
            />
          ))}
          {(searchResults ?? []).length === 0 && (
            <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 48, marginBottom: 10 }}>🔍</div>
              {searchFetching ? 'Ricerca in corso…' : `Nessun risultato per "${searchTerm.trim()}"`}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* ---------- Breadcrumb navigabile ---------- */}
          <div className="folder-breadcrumb">
            <button className="btn-secondary" onClick={goBack} disabled={!currentFolderId}
              style={{ padding: '6px 12px', fontSize: 13 }}>⬅ Indietro</button>
            <span className="breadcrumb-path">
              <button className="breadcrumb-seg" onClick={() => setCurrentFolderId(null)}>📁 Home</button>
              {segments.map(s => (
                <span key={s.id}>
                  <span className="breadcrumb-sep">\</span>
                  <button className="breadcrumb-seg" onClick={() => setCurrentFolderId(s.id)}>{s.name}</button>
                </span>
              ))}
            </span>
          </div>

          {/* ---------- Griglia cartelle (2 per fila, stile Windows) ---------- */}
          {(folders ?? []).length > 0 && (
            <div className="folder-grid">
              {(folders ?? []).map(f => (
                <div key={f.id} className="folder-card" onClick={() => setCurrentFolderId(f.id)}>
                  <span className="folder-icon">{f.hasContent ? '📁' : '📂'}</span>
                  <span className="folder-name">{f.name}</span>
                  <button className="btn-secondary folder-rename"
                    onClick={e => { e.stopPropagation(); openFolderModal('rename', f); }}>✏️ Rinomina</button>
                </div>
              ))}
            </div>
          )}

          {/* ---------- Lista file del livello corrente ---------- */}
          <div className="doc-grid">
            {(documents ?? []).map(d => (
              <DocumentCard key={d.id} doc={d} onOpen={openDocument} onDelete={deleteDocument} />
            ))}
            {(documents ?? []).length === 0 && (folders ?? []).length === 0 && (
              <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 48, marginBottom: 10 }}>📁</div>
                Cartella vuota — crea una sottocartella o carica un file
              </div>
            )}
          </div>
        </>
      )}

      {/* ---------- Modal crea/rinomina cartella ---------- */}
      {folderModal && (
        <div className="modal-overlay" onClick={() => setFolderModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>
              {folderModal.mode === 'create' ? '📁 Nuova cartella' : '✏️ Rinomina cartella'}
            </h3>
            <input
              type="text"
              autoFocus
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void submitFolder(); }}
              placeholder="Nome cartella"
              style={{ width: '100%', padding: 10, margin: '4px 0 16px',
                       border: '1px solid #ced4da', borderRadius: 6, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setFolderModal(null)} className="btn-secondary"
                style={{ padding: '8px 16px' }}>Annulla</button>
              <button onClick={() => void submitFolder()} className="btn-success"
                style={{ padding: '8px 16px' }} disabled={!folderName.trim()}>
                {folderModal.mode === 'create' ? 'Crea' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
